import { FontData } from '@lidojs/design-core';
import { RiCloseLine, RiSearchLine, RiCheckLine } from '@remixicon/react';
import React, {
  ChangeEvent,
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAsync } from 'react-use';
import { EditorContext } from '../../editor/EditorContext';
import { useEditor } from '../../hooks';
import { useUsedFont } from '../../layers/hooks/useUsedFont';
import FontStyle from './FontStyle';
import Sidebar, { SidebarProps } from './Sidebar';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

interface FontSidebarProps extends SidebarProps {
  selected: FontData[];
  onChangeFontFamily: (font: FontData) => void;
}

const FontSidebar: ForwardRefRenderFunction<
  HTMLDivElement,
  FontSidebarProps
> = ({ selected, onChangeFontFamily, ...props }, ref) => {
  const { t } = useTranslation();
  const dataRef = useRef(false);
  const qRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { getFonts } = useContext(EditorContext);
  const { usedFonts } = useUsedFont();
  const { actions, fontList } = useEditor((state) => ({
    fontList: state.fontList,
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [keyword, setKeyword] = useState('');

  const loadFontList = useCallback(
    async (offset = 0) => {
      dataRef.current = true;
      setIsLoading(true);
      const res = await getFonts({
        limit: '20',
        offset: offset + '',
        q: keyword,
      });
      if (offset) {
        actions.appendFontList(res);
      } else {
        actions.setFontList(res);
      }
      setIsLoading(false);
      if (res.length > 0) {
        dataRef.current = false;
      }
    },
    [getFonts, actions, setIsLoading, keyword]
  );

  useAsync(async () => {
    await loadFontList();
  }, [loadFontList]);

  useEffect(() => {
    const handleLoadMore = async (e: Event) => {
      const node = e.target as HTMLDivElement;
      if (
        node.scrollHeight - node.scrollTop - 80 <= node.clientHeight &&
        !dataRef.current
      ) {
        await loadFontList(fontList.length);
      }
    };
    const scrollEle = scrollRef.current;
    scrollEle?.addEventListener('scroll', handleLoadMore);
    return () => {
      scrollEle?.removeEventListener('scroll', handleLoadMore);
    };
  }, [loadFontList, fontList]);

  const handleSearch = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    setKeyword(qRef.current?.value || '');
    await loadFontList(0);
  };

  return (
    <Sidebar ref={ref} {...props}>
      <FontStyle />
      <div
        css={(theme) => ({
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        })}
      >
        <div
          css={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            height: 48,
            borderBottom: `1px solid ${theme.colors.stroke}`,
            padding: '0 20px',
          })}
        >
          <p
            css={(theme) => ({
              lineHeight: '48px',
              fontWeight: 600,
              color: theme.colors.content,
              flexGrow: 1,
            })}
          >
            Font
          </p>
          <div
            css={(theme) => ({
              fontSize: 20,
              flexShrink: 0,
              width: 32,
              height: 32,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
            onClick={() => {
              actions.setSidebar();
            }}
          >
            <RiCloseLine />
          </div>
        </div>
        <div
          css={(theme) => ({
            borderRadius: 4,
            border: `1px solid ${theme.colors.stroke}`,
            margin: 16,
          })}
        >
          <div
            css={(theme) => ({
              height: 40,
              borderRadius: 4,
              paddingLeft: 12,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            })}
          >
            <div
              css={(theme) => ({
                fontSize: 24,
                marginRight: 8,
                flexShrink: 0,
              })}
            >
              <RiSearchLine size={20} />
            </div>
            <input
              ref={qRef}
              css={(theme) => ({
                width: '100%',
                height: '100%',
                backgroundColor: theme.colors.input,
                paddingLeft: 8,
              })}
              type={'text'}
              onInput={debounce(handleSearch, 400)}
            />
          </div>
        </div>
        <div
          ref={scrollRef}
          css={(theme) => ({
            flexGrow: 1,
            overflowY: 'auto',
          })}
        >
          <div
            css={(theme) => ({
              padding: '16px 20px',
              fontWeight: 700,
            })}
          >
            {t('page_editor.text.document_fonts')}
          </div>
          {usedFonts.map((font) => (
            <div
              key={font.name}
              css={(theme) => ({
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '0 24px',
                ':hover': {
                  background:
                    theme.name === 'dark'
                      ? theme.colors.backgroundItem
                      : '#F9F9F9',
                },
              })}
              onClick={() => onChangeFontFamily(font)}
            >
              <span
                css={(theme) => ({
                  fontFamily: font.name,
                })}
              >
                {font.name}
              </span>
              {selected.map((s) => s.name).includes(font.name) && (
                <span>
                  <RiCheckLine size={18} />
                </span>
              )}
            </div>
          ))}
          <div
            css={(theme) => ({
              borderTop: `1px solid ${theme.colors.stroke}`,
            })}
          >
            <div
              css={(theme) => ({
                padding: '16px 20px',
                fontWeight: 700,
              })}
            >
              Fonts
            </div>
            {fontList.map((font) => (
              <div
                key={font.name}
                css={(theme) => ({
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  padding: '0 24px',
                  ':hover': {
                    background:
                      theme.name === 'dark'
                        ? theme.colors.backgroundItem
                        : '#F9F9F9',
                  },
                })}
                onClick={() => onChangeFontFamily(font)}
              >
                <span
                  css={(theme) => ({
                    fontFamily: font.name,
                  })}
                >
                  {font.name}
                </span>
                {selected.map((s) => s.name).includes(font.name) && (
                  <span>
                    <RiCheckLine size={18} />
                  </span>
                )}
              </div>
            ))}
            {isLoading && <div>Loading...</div>}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default forwardRef<HTMLDivElement, FontSidebarProps>(FontSidebar);
